import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, SearchX } from 'lucide-react';
import { Avatar, Card, EmptyState, SearchInput } from '@/components/ds';
import { useAuth } from '@/contexts/AuthContext';
import { settingsSectionPath } from '@/routes/route-config';
import { cn } from '@/utils/cn';
import {
  groupSections,
  visibleSections,
  type SettingsSectionSpec,
} from './sections';

/**
 * `/dashboard/settings`: every section the user may open, grouped, with a
 * filter on top.
 *
 * This used to redirect to Profile, so "Settings" opened a page about the
 * signed-in person and the clinic's own configuration was only reachable from
 * a 22-row accordion in the rail. The filter matches the section's
 * description as well as its name, so "hours" finds Availability and "logo"
 * finds Clinic, even though descriptions are not shown.
 */
export function SettingsHub() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { permissions, user } = useAuth();
  const [query, setQuery] = useState('');

  const groups = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    const sections = visibleSections(permissions).filter(
      (spec) =>
        !needle ||
        [spec.labelKey, spec.descriptionKey].some((key) =>
          t(key).toLocaleLowerCase().includes(needle),
        ),
    );
    return groupSections(sections);
  }, [permissions, query, t]);

  const firstMatch = groups[0]?.sections[0];

  return (
    <div className="space-y-4 lg:space-y-6">
      <SearchInput
        value={query}
        onChange={setQuery}
        // Enter opens the first match, so the filter doubles as a jump box.
        onSubmit={() =>
          firstMatch && navigate(settingsSectionPath(firstMatch.id))
        }
        placeholder={t('settings.hub.searchPlaceholder')}
        className="max-w-md"
      />

      {groups.length === 0 ? (
        <EmptyState
          icon={SearchX}
          title={t('settings.hub.noMatch', { query: query.trim() })}
        />
      ) : (
        <div className="grid items-start gap-4 md:grid-cols-2 lg:gap-6 xl:grid-cols-3">
          {groups.map((group) => (
            <section
              key={group.id}
              aria-labelledby={`settings-group-${group.id}`}
            >
              <h2
                id={`settings-group-${group.id}`}
                className="px-1 pb-2 text-micro font-semibold uppercase tracking-wider text-secondary-400"
              >
                {t(group.labelKey)}
              </h2>
              <Card className="overflow-hidden">
                <ul className="divide-y divide-border-subtle">
                  {group.sections.map((spec) => (
                    <li key={spec.id}>
                      <SectionRow
                        spec={spec}
                        label={t(spec.labelKey)}
                        // Profile is the one row about a person; it shows
                        // who, so it is not mistaken for the clinic's profile.
                        person={
                          spec.id === 'profile' && user
                            ? {
                                name: user.name,
                                email: user.email,
                                photoUrl: user.photoUrl,
                              }
                            : undefined
                        }
                      />
                    </li>
                  ))}
                </ul>
              </Card>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function SectionRow({
  spec,
  label,
  person,
}: {
  spec: SettingsSectionSpec;
  label: string;
  person?: { name: string; email: string; photoUrl?: string | null };
}) {
  const Icon = spec.icon;
  return (
    <Link
      to={settingsSectionPath(spec.id)}
      className={cn(
        'group flex items-center gap-3 px-4 py-3',
        'no-underline transition-colors hover:no-underline hover:bg-surface-2',
        'outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/60',
      )}
    >
      {person ? (
        <Avatar name={person.name} src={person.photoUrl} size="sm" />
      ) : (
        <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-brand-soft text-brand-text">
          <Icon aria-hidden className="size-4" />
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-body font-medium text-foreground">
          {label}
        </span>
        {person && (
          <span className="block truncate text-small text-muted">
            {person.email}
          </span>
        )}
      </span>
      <ChevronRight
        aria-hidden
        className="size-4 shrink-0 text-muted transition-colors group-hover:text-foreground rtl:-scale-x-100"
      />
    </Link>
  );
}
